import { reviewSubscription } from './subscription_checkout';
import axios from "axios";

/**
 * PayU Payment Service
 *
 * Handles payment initiation and verification for Pro subscriptions.
 *
 * Flow:
 * 1. Frontend calls `initiatePayment()` with plan + user info
 * 2. Backend generates a secure hash and returns form params
 * 3. Frontend auto-submits a form to PayU's payment gateway
 * 4. PayU redirects back to success/failure URL
 * 5. Backend verifies the response hash and activates subscription
 *
 * Replace the constants below with your real PayU credentials.
 */

const API_BASE =
  process.env.VUE_APP_API_BASE || window.location.origin;

/**
 * Plan definitions
 */
export const PLANS = {
  monthly: {
    id: "pro_monthly",
    name: "Pro Monthly",
    amount: "1000.00",
    description: "TradingCandle Pro — Monthly",
    duration: 30, // days
  },
  yearly: {
    id: "pro_yearly",
    name: "Pro Yearly",
    amount: "10000.00",
    description: "TradingCandle Pro — Yearly",
    duration: 365, // days
  },
};

/**
 * Initiate a PayU payment.
 *
 * Calls the backend to generate a hash, then auto-submits a form
 * to PayU's payment page.
 *
 * @param {string} planKey  — 'monthly' or 'yearly'
 * @param {object} user     — { id, email, firstName, lastName }
 * @param {string} token    — Clerk session JWT for backend auth
 */
export async function initiatePayment(planKey, user, token, aiOnly = false) {
  const { data: catalog } = await axios.get(`${API_BASE}/api/payu/catalog`, { timeout: 20000, headers: { Authorization: `Bearer ${token}` } });
  const plan = catalog.plans[planKey];
  if (!plan) throw new Error("Invalid plan");
  const selection = await reviewSubscription({ name: plan.name, price: Number(plan.amount), packs: catalog.aiPacks,
    aiOnly, duration: plan.durationDays, trialCredits: catalog.trialCredits });

  // Ask the backend to generate the secure hash
  const { data } = await axios.post(
    `${API_BASE}/api/payu/initiate`,
    {
      aiPack: selection.aiPack,
      aiOnly,
      planKey,
      amount: plan.amount,
      productInfo: plan.description,
      firstName: user.firstName || user.fullName || "User",
      email: user.email,
      userId: user.id,
    },
    {
      timeout: 20000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!data.success) {
    throw new Error(data.error || "Failed to initiate payment");
  }

  // Build and auto-submit the PayU form
  _submitPayUForm(data.params, data.action);
}

/**
 * Verify a PayU payment response (called on the success callback page).
 *
 * @param {object} payuResponse — query/body params from PayU redirect
 * @param {string} token        — Clerk session JWT
 * @returns {Promise<object>}   — { success, subscription }
 */
export async function verifyPayment(payuResponse, token) {
  const { data } = await axios.post(
    `${API_BASE}/api/payu/verify`,
    payuResponse,
    {
      timeout: 20000,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return data;
}

/**
 * Check current subscription status from the backend.
 *
 * @param {string} token — Clerk session JWT
 * @returns {Promise<object>} — { active, plan, expiresAt }
 */
export async function checkSubscription(token) {
  const { data } = await axios.get(
    `${API_BASE}/api/subscription/status`,
    {
      timeout: 20000,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
}

/* ── internal ── */

function _submitPayUForm(params, action) {
  if (!['https://secure.payu.in/_payment', 'https://test.payu.in/_payment'].includes(action)) throw new Error('Invalid payment gateway');
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  form.style.display = "none";

  for (const [key, value] of Object.entries(params)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = value || "";
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

export default {
  PLANS,
  initiatePayment,
  verifyPayment,
  checkSubscription,
};
